import { Link, NavLink, Outlet, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useSession } from '../../lib/session';
import { setLocale } from '../../i18n';
import { Badge, Button } from '../ui';

export function AppShell() {
  const { t, i18n } = useTranslation();
  const { identity, logout, switchOrg } = useSession();
  const navigate = useNavigate();
  const nav = 'rounded-md px-3 py-1.5 text-sm font-medium hover:bg-slate-100';
  const active = ({ isActive }: { isActive: boolean }) =>
    isActive ? `${nav} bg-slate-100 text-emerald-700` : `${nav} text-slate-700`;
  return (
    <div className="flex min-h-screen flex-col">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl items-center gap-3 px-4 py-3">
          <Link to="/" className="flex items-center gap-2">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-emerald-600 font-bold text-white">
              F
            </span>
            <span className="font-semibold">{t('app.name')}</span>
          </Link>
          {identity && (identity.superAdmin ? identity.orgs.length > 0 : identity.orgs.length > 1) ? (
            <select
              aria-label={t('orgSwitch.label')}
              className="max-w-56 rounded-md border border-slate-300 bg-white px-2 py-1 text-sm"
              value={identity.org?.id ?? ''}
              onChange={(e) => {
                const id = e.target.value;
                void switchOrg(id === identity.homeOrgId ? null : id).then(() => navigate('/'));
              }}
            >
              {!identity.org && <option value="">-</option>}
              {identity.orgs.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                  {o.home ? ` (${t('orgSwitch.home')})` : ''}
                  {o.status !== 'active' ? ` · ${o.status}` : ''}
                </option>
              ))}
            </select>
          ) : (
            identity?.org && <span className="text-sm text-slate-500">/ {identity.org.name}</span>
          )}
          <nav className="ml-4 flex gap-1">
            {identity?.state === 'member' && (
              <>
                <NavLink to="/" end className={active}>
                  {t('nav.dashboard')}
                </NavLink>
                <NavLink to="/products" className={active}>
                  {t('nav.products')}
                </NavLink>
                <NavLink to="/members" className={active}>
                  {t('nav.members')}
                </NavLink>
              </>
            )}
            {identity?.superAdmin && (
              <NavLink to="/superadmin" className={active}>
                {t('nav.superadmin')}
              </NavLink>
            )}
          </nav>
          <div className="ml-auto flex items-center gap-2 text-sm">
            <select
              aria-label={t('app.language')}
              className="rounded-md border border-slate-300 bg-white px-1 py-1 text-xs"
              value={i18n.language.startsWith('ko') ? 'ko' : 'en'}
              onChange={(e) => setLocale(e.target.value as 'en' | 'ko')}
            >
              <option value="en">EN</option>
              <option value="ko">KO</option>
            </select>
            {identity && (
              <>
                <span className="hidden text-slate-600 sm:inline">{identity.user.email}</span>
                {!identity.supportMode &&
                  identity.roles.map((r) => (
                    <Badge key={r} tone="blue">
                      {t(`roles.${r}`)}
                    </Badge>
                  ))}
                {identity.superAdmin && <Badge tone="amber">SuperAdmin</Badge>}
                <Button variant="ghost" onClick={() => void logout()}>
                  {t('app.signOut')}
                </Button>
              </>
            )}
          </div>
        </div>
      </header>
      {identity?.supportMode && identity.org && (
        <div className="border-b border-amber-200 bg-amber-50">
          <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-3 px-4 py-2 text-sm text-amber-900">
            <Badge tone="amber">{t('orgSwitch.supportMode')}</Badge>
            <span className="flex-1">{t('orgSwitch.supportModeBody', { name: identity.org.name })}</span>
            <Button variant="secondary" onClick={() => void switchOrg(null).then(() => navigate('/'))}>
              {t('orgSwitch.backHome')}
            </Button>
          </div>
        </div>
      )}
      <main className="mx-auto w-full max-w-6xl flex-1 px-4 py-8">
        <Outlet />
      </main>
      <footer className="border-t border-slate-200 py-4 text-center text-xs text-slate-500">
        {t('app.disclaimer')}
      </footer>
    </div>
  );
}
